import toast, { Toaster, Toast, resolveValue } from "react-hot-toast";
import { 
    WarningSignIcon, 
    CrossIcon, 
    InfoSignIcon, 
    TickCircleIcon,
    BanCircleIcon
} from "evergreen-ui";

// Add CSS for animations - this runs once when the module is loaded
const injectToastStyles = () => {
    // Only add the styles once
    if (!document.getElementById('toast-animation-styles')) {
        const style = document.createElement('style');
        style.id = 'toast-animation-styles';
        style.textContent = `
            @keyframes toast-enter {
                0% { transform: translateY(-20px); opacity: 0; }
                100% { transform: translateY(0); opacity: 1; }
            }
            
            @keyframes toast-exit {
                0% { transform: translateY(0); opacity: 1; }
                100% { transform: translateY(-20px); opacity: 0; }
            }
            
            .toast-enter-animation {
                animation: toast-enter 0.35s ease-out forwards;
            }
            
            .toast-exit-animation {
                animation: toast-exit 0.3s ease-in forwards;
            }
        `;
        document.head.appendChild(style);
    }
};

// Call it immediately
injectToastStyles();

// Toast style constants
const TOAST_STYLES = {
    default: {
        iconBgColor: "#5ea832", // Green background for icon section
        iconColor: "#ffffff", // White icon
        Icon: InfoSignIcon
    },
    success: {
        iconBgColor: "#47B881", // Evergreen success green
        iconColor: "#ffffff",
        Icon: TickCircleIcon
    },
    error: {
        iconBgColor: "#EC4C47", // Evergreen danger red
        iconColor: "#ffffff",
        Icon: BanCircleIcon
    },
    loading: {
        iconBgColor: "#3366FF", // Evergreen blue
        iconColor: "#ffffff",
        Icon: InfoSignIcon
    },
    warning: {
        iconBgColor: "#D9822B", // Evergreen warning orange
        iconColor: "#ffffff",
        Icon: WarningSignIcon
    }
};

// Helper function to determine the appropriate styling based on toast type
const getToastStyles = (t: Toast) => {
    // Default styles
    if (t.type === 'success') return TOAST_STYLES.success;
    if (t.type === 'error') return TOAST_STYLES.error;
    if (t.type === 'loading') return TOAST_STYLES.loading;
    if (t.type === 'blank' && t.icon === '🔔') return TOAST_STYLES.warning;
    
    return TOAST_STYLES.default;
};

// Helper function to handle toast dismissal with animation
const dismissToastWithAnimation = (toastId: string) => {
    const toastElement = document.getElementById(toastId);
    if (toastElement) {
        toastElement.classList.remove('toast-enter-animation');
        toastElement.classList.add('toast-exit-animation');
    }
    
    // Delay the actual dismissal to allow animation to play
    setTimeout(() => {
        toast.dismiss(toastId);
    }, 300);
};

// Toast UI styling constants
const TOAST_UI = {
    container: {
        backgroundColor: "#ffffff",
        borderRadius: "4px",
        boxShadow: "0 3px 10px rgba(0, 0, 0, 0.2)"
    },
    closeButton: {
        color: "#66788A" // Gray color for close button
    }
};

export const NotificationToaster = () => {
    return (
        <Toaster
            position="top-center"
            reverseOrder={true}
            toastOptions={{
                duration: 5000,
                className: "toast-enter-animation",
            }}
        >
            {(t) => {
                const { iconBgColor, iconColor, Icon } = getToastStyles(t);
                const animationClass = t.visible ? 'toast-enter-animation' : 'toast-exit-animation';
                
                return (
                    <div 
                        className={`relative overflow-hidden ${animationClass} max-w-xl text-sm cursor-pointer shadow-md`}
                        style={TOAST_UI.container}
                        onClick={() => dismissToastWithAnimation(t.id)}
                    >
                        <div className="flex items-stretch">
                            {/* Colored icon section */}
                            <div 
                                className="flex items-center justify-center w-16 self-stretch" 
                                style={{ backgroundColor: iconBgColor, color: iconColor }}
                            >
                                <Icon size={24} />
                            </div>
                            
                            {/* Message with black text */}
                            <div className="flex-1 font-medium text-gray-800 py-4 px-4 whitespace-pre-line">
                                {resolveValue(t.message, t)}
                            </div>
                            
                            {/* Close button */}
                            <div 
                                onClick={(e) => {
                                    e.stopPropagation(); // Prevent bubbling
                                    dismissToastWithAnimation(t.id);
                                }}
                                className="flex-shrink-0 hover:opacity-70 p-4 cursor-pointer"
                                style={TOAST_UI.closeButton}
                            >
                                <CrossIcon size={20} />
                            </div>
                        </div>
                    </div>
                );
            }}
        </Toaster>
    );
};



// Export convenience methods to show different types of toasts
type ToastOptions = {
    // Duration in seconds
    duration?: number;
}

// Helper to convert seconds to milliseconds and apply default duration
const getDurationMs = (options?: ToastOptions): number => {
    const durationSec = options?.duration ?? 5;
    return durationSec * 1000;
};

export const showToast = {
    // Show a notification toast (info style)
    notify: (message: string, options?: ToastOptions) => {
        return toast(message, { duration: getDurationMs(options) });
    },
    
    // Show a success toast
    success: (message: string, options?: ToastOptions) => {
        return toast.success(message, { duration: getDurationMs(options) });
    },
    
    // Show a warning toast (custom styled)
    warning: (message: string, options?: ToastOptions) => {
        // Use a custom icon to indicate this is a warning
        return toast(message, { 
            duration: getDurationMs(options),
            icon: '🔔' // This will be used to identify warning toasts
        });
    },
    
    // Show an error toast
    error: (message: string, options?: ToastOptions) => {
        return toast.error(message, { duration: getDurationMs(options) });
    },

    // Show a warning toast with a React component (supports JSX with clickable links)
    // No current consumers; kept for reuse.
    // warningWithComponent: (component: React.ReactElement | string, options?: ToastOptions) => {
    //     return toast(component as any, {
    //         duration: getDurationMs(options),
    //         icon: '🔔'
    //     });
    // },
    
    // Close all toasts
    closeAll: () => {
        toast.dismiss();
    }
};